import { importWithRetry } from '../chunkRetry/chunkRetry';
import {
  PRE_APP_EVENT,
  serializePreAppError,
  showAppLoadError,
  trackPreApp,
} from '../preAppBeacon/preAppBeacon';

// Entry-point wrapper for `main.js`: retries the bootstrap chunk on a transient
// download failure, reports every attempt through the pre-app beacon, and falls
// back to the friendly error screen instead of leaving the document blank.
// Never rejects — a rejection here would only surface as an unhandled promise
// with nothing left to handle it.
export const startAppEntry = ({
  load,
  appScope,
  retryOptions = {},
  deps = {},
} = {}) => {
  if (typeof load !== 'function') {
    throw new Error('startAppEntry: `load` is required.');
  }

  const _trackPreApp = deps.trackPreApp || trackPreApp;
  const _showAppLoadError = deps.showAppLoadError || showAppLoadError;
  const _importWithRetry = deps.importWithRetry || importWithRetry;
  const _console = deps.console || console;

  let retried = 0;

  return _importWithRetry(load, {
    ...retryOptions,
    onRetry: ({ attempt, retries, delayMs, error }) => {
      retried = attempt;
      _trackPreApp(PRE_APP_EVENT.BOOTSTRAP_RETRY, {
        appScope,
        attempt,
        retries,
        delayMs,
        ...serializePreAppError(error),
      });
    },
  })
    .then((module) => {
      if (retried > 0) {
        _trackPreApp(PRE_APP_EVENT.BOOTSTRAP_RECOVERED, {
          appScope,
          attempts: retried,
        });
      }
      return module;
    })
    .catch((error) => {
      _console.error('[You][pre-app] bootstrap failed', error);
      _trackPreApp(PRE_APP_EVENT.BOOTSTRAP_FAILED, {
        appScope,
        attempts: retried,
        ...serializePreAppError(error),
      });
      _showAppLoadError({ reason: PRE_APP_EVENT.BOOTSTRAP_FAILED, appScope });
      return null;
    });
};

// Same contract for a Module Federation remote loaded by the host. It rejects
// on purpose: the caller renders a React fallback for the failed route, which
// the host can still do because its own bundle is alive.
export const loadRemoteWithRetry = ({
  load,
  remoteKey,
  retryOptions = {},
  deps = {},
} = {}) => {
  if (typeof load !== 'function') {
    throw new Error('loadRemoteWithRetry: `load` is required.');
  }

  const _trackPreApp = deps.trackPreApp || trackPreApp;
  const _importWithRetry = deps.importWithRetry || importWithRetry;

  let retried = 0;

  return _importWithRetry(load, {
    ...retryOptions,
    onRetry: ({ attempt, retries, delayMs, error }) => {
      retried = attempt;
      _trackPreApp(PRE_APP_EVENT.REMOTE_RETRY, {
        remoteKey,
        attempt,
        retries,
        delayMs,
        ...serializePreAppError(error),
      });
    },
  })
    .then((module) => {
      if (retried > 0) {
        _trackPreApp(PRE_APP_EVENT.REMOTE_RECOVERED, {
          remoteKey,
          attempts: retried,
        });
      }
      return module;
    })
    .catch((error) => {
      _trackPreApp(PRE_APP_EVENT.REMOTE_FAILED, {
        remoteKey,
        attempts: retried,
        ...serializePreAppError(error),
      });
      throw error;
    });
};
